import React from "react";
import styled from "styled-components";
import { Button, Tag } from "../../../../../design-system";
import {
  AdSwipeDoc,
  AdSwipeStatus,
  swipeRunDays,
} from "../../../../../database/community/adEngine";
import { ExternalLink, Muted, Row, RowList } from "../shared";
import Modal from "../Modal";
import { PROVEN_RUN_DAYS } from "./AdsDashboard";

// Everything we know about one competitor ad, plus triage actions. Recreate
// is a guided handoff: shortlist the swipe, open the live ad, and land in
// the ads generator where the coach drops the creative in as a reference
// (ad library pages cannot be mirrored client-side).

const BodyText = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  line-height: 1.6;
  white-space: pre-wrap;
`;

const Creative = styled.img`
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  border-radius: var(--radius-lg);
  background: var(--color-surface-subtle);
`;

const TooNew = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-warning, #d0a020);
  line-height: 1.5;
`;

const Field = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  min-width: 110px;
`;

const STATUS_VARIANT: Record<string, "neutral" | "accent" | "success" | "warning"> = {
  new: "neutral",
  shortlisted: "warning",
  recreated: "success",
  dismissed: "neutral",
};

export type SwipeDetailModalProps = {
  swipe: AdSwipeDoc;
  onClose: () => void;
  onSetStatus: (id: string, status: AdSwipeStatus) => void;
  onRecreate: (swipe: AdSwipeDoc) => void;
};

export const SwipeDetailModal: React.FC<SwipeDetailModalProps> = ({
  swipe,
  onClose,
  onSetStatus,
  onRecreate,
}) => {
  const days = swipeRunDays(swipe);
  return (
    <Modal title={swipe.advertiserName} onClose={onClose}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <Tag variant="neutral" size="sm">
          {swipe.platform === "meta" ? "Meta" : swipe.platform}
        </Tag>
        <Tag variant={swipe.active ? "success" : "neutral"} size="sm">
          {swipe.active ? `running ${days}d` : `ran ${days}d, stopped`}
        </Tag>
        <Tag variant={STATUS_VARIANT[swipe.status] || "neutral"} size="sm">
          {swipe.status}
        </Tag>
      </div>

      {swipe.storedCreatives.map((c, i) =>
        c.contentType.startsWith("video/") ? (
          <video key={i} src={c.url} controls style={{ width: "100%", borderRadius: 12 }} />
        ) : (
          <Creative key={i} src={c.url} alt="" />
        )
      )}

      <BodyText>{swipe.title || swipe.bodyText || "No ad copy captured."}</BodyText>
      {swipe.title && swipe.bodyText && <BodyText>{swipe.bodyText}</BodyText>}

      {swipe.active && days < PROVEN_RUN_DAYS && (
        <TooNew>
          Too new to judge. This ad has run {days === 0 ? "less than a day" : `${days} day${days === 1 ? "" : "s"}`}.
          An ad only proves itself by staying live for weeks. Watch it before copying.
        </TooNew>
      )}

      <RowList>
        {swipe.firstSeenAt && (
          <Row>
            <Field>First seen</Field>
            <Muted>{new Date(swipe.firstSeenAt).toLocaleDateString()}</Muted>
          </Row>
        )}
        {swipe.lastSeenAt && (
          <Row>
            <Field>Last confirmed</Field>
            <Muted>{new Date(swipe.lastSeenAt).toLocaleDateString()}</Muted>
          </Row>
        )}
        {swipe.cta && (
          <Row>
            <Field>Call to action</Field>
            <Muted>{swipe.cta}</Muted>
          </Row>
        )}
        {swipe.countries.length > 0 && (
          <Row>
            <Field>Countries</Field>
            <Muted>{swipe.countries.join(", ")}</Muted>
          </Row>
        )}
        {swipe.landingUrl && (
          <Row>
            <Field>Landing page</Field>
            <ExternalLink href={swipe.landingUrl}>{swipe.landingUrl}</ExternalLink>
          </Row>
        )}
        {swipe.notes && (
          <Row>
            <Field>Notes</Field>
            <Muted>{swipe.notes}</Muted>
          </Row>
        )}
      </RowList>

      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
        {swipe.snapshotUrl && (
          <ExternalLink href={swipe.snapshotUrl}>
            <Button variant="secondary" size="sm">
              View live ad
            </Button>
          </ExternalLink>
        )}
        <Button variant="primary" size="sm" onClick={() => onRecreate(swipe)}>
          Recreate this ad
        </Button>
        {swipe.status !== "shortlisted" && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onSetStatus(swipe.id, "shortlisted")}
          >
            Shortlist
          </Button>
        )}
        {swipe.status !== "dismissed" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onSetStatus(swipe.id, "dismissed")}
          >
            Dismiss
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default SwipeDetailModal;
